import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Global store for rehearsal state
 * Centralized state management for:
 * - Current song/section context
 * - BPM and metronome settings
 * - Session logs for analytics
 * - Ready for future "Director Mode" synchronization
 */
export const useRehearsalStore = create(
  persist(
    (set, get) => ({
      // Current context
      currentSetlistId: null,
      currentSongIndex: 0,
      currentSectionIndex: 0,
      
      // Metronome state
      bpm: 120,
      isPlaying: false,
      volume: 70,
      beatsPerBar: 4,
      currentBeat: 0,
      
      // Loop state
      isLooping: false,
      loopSectionIndex: null,
      
      // Tap tempo
      tapTimes: [],
      
      // Session logs for analytics
      sessionLogs: [],
      currentSessionId: null,
      sessionStartTime: null,
      
      // Actions - Context
      setCurrentSetlist: (setlistId) => set({ 
        currentSetlistId: setlistId,
        currentSongIndex: 0,
        currentSectionIndex: 0 
      }),
      
      setCurrentSong: (index, songBpm, timeSignature) => {
        const beats = parseInt(timeSignature?.split('/')[0]) || 4;
        set({ 
          currentSongIndex: index,
          currentSectionIndex: 0,
          bpm: songBpm || get().bpm,
          beatsPerBar: beats,
          currentBeat: 0
        });
        get().logEvent('song_change', { songIndex: index, bpm: songBpm });
      },
      
      setCurrentSection: (index) => {
        set({ currentSectionIndex: index, currentBeat: 0 });
        if (get().isLooping) {
          set({ loopSectionIndex: index });
        }
        get().logEvent('section_change', { sectionIndex: index });
      },
      
      // Actions - Metronome
      setBpm: (newBpm) => {
        const clampedBpm = Math.max(30, Math.min(280, newBpm));
        set({ bpm: clampedBpm });
        get().logEvent('bpm_change', { bpm: clampedBpm });
      },
      
      adjustBpm: (delta) => {
        const newBpm = Math.max(30, Math.min(280, get().bpm + delta));
        set({ bpm: newBpm });
      },
      
      setPlaying: (playing) => {
        set({ isPlaying: playing });
        if (playing && !get().currentSessionId) {
          get().startSession();
        }
        get().logEvent(playing ? 'play' : 'pause', { bpm: get().bpm });
      },
      
      setVolume: (vol) => set({ volume: vol }),
      
      setCurrentBeat: (beat) => set({ currentBeat: beat }),
      
      incrementBeat: () => set(state => ({ 
        currentBeat: state.currentBeat + 1 
      })),
      
      resetBeat: () => set({ currentBeat: 0 }),
      
      // Actions - Loop
      toggleLoop: () => {
        const newLooping = !get().isLooping;
        set({ 
          isLooping: newLooping,
          loopSectionIndex: newLooping ? get().currentSectionIndex : null
        });
        get().logEvent('loop_toggle', { enabled: newLooping, sectionIndex: get().currentSectionIndex });
      },
      
      // Actions - Tap Tempo
      tapTempo: () => {
        const now = Date.now();
        const { tapTimes } = get();
        
        // Reset if more than 2 seconds since last tap
        if (tapTimes.length > 0 && now - tapTimes[tapTimes.length - 1] > 2000) {
          set({ tapTimes: [now] });
          return null;
        }
        
        const newTaps = [...tapTimes, now].slice(-4); // Keep last 4 taps
        set({ tapTimes: newTaps });
        
        if (newTaps.length >= 2) {
          // Calculate average interval
          let totalInterval = 0;
          for (let i = 1; i < newTaps.length; i++) {
            totalInterval += newTaps[i] - newTaps[i - 1];
          }
          const avgInterval = totalInterval / (newTaps.length - 1);
          const calculatedBpm = Math.round(60000 / avgInterval);
          const clampedBpm = Math.max(30, Math.min(280, calculatedBpm));
          
          set({ bpm: clampedBpm });
          get().logEvent('tap_tempo', { bpm: clampedBpm, taps: newTaps.length });
          return clampedBpm;
        }
        return null;
      },
      
      clearTapTempo: () => set({ tapTimes: [] }),
      
      // Session logging for analytics
      startSession: () => {
        const sessionId = `session_${Date.now()}`;
        set({ 
          currentSessionId: sessionId,
          sessionStartTime: Date.now(),
          sessionLogs: []
        });
      },
      
      endSession: () => {
        const { currentSessionId, sessionStartTime, sessionLogs, currentSetlistId } = get();
        if (currentSessionId) {
          const duration = Date.now() - sessionStartTime;
          // Could persist to backend here
          console.log('Session ended:', {
            id: currentSessionId,
            setlistId: currentSetlistId,
            duration,
            eventCount: sessionLogs.length,
            logs: sessionLogs
          });
        }
        set({ 
          currentSessionId: null,
          sessionStartTime: null,
          isPlaying: false,
          currentBeat: 0
        });
      },
      
      logEvent: (type, data) => {
        if (!get().currentSessionId) return;
        
        const event = {
          type,
          timestamp: Date.now(),
          elapsed: Date.now() - get().sessionStartTime,
          ...data
        };
        set(state => ({ 
          sessionLogs: [...state.sessionLogs, event]
        }));
      },
      
      // Get session stats
      getSessionStats: () => {
        const { sessionLogs, sessionStartTime } = get();
        if (!sessionStartTime) return null;
        
        const sectionChanges = sessionLogs.filter(l => l.type === 'section_change');
        const sectionCounts = {};
        sectionChanges.forEach(sc => {
          sectionCounts[sc.sectionIndex] = (sectionCounts[sc.sectionIndex] || 0) + 1;
        });
        
        return {
          duration: Date.now() - sessionStartTime,
          totalEvents: sessionLogs.length,
          sectionPracticed: Object.keys(sectionCounts).length,
          mostPracticedSection: Object.entries(sectionCounts)
            .sort((a, b) => b[1] - a[1])[0]?.[0]
        };
      },
      
      // Reset all
      reset: () => set({
        currentSetlistId: null,
        currentSongIndex: 0,
        currentSectionIndex: 0,
        bpm: 120,
        isPlaying: false,
        volume: 70,
        beatsPerBar: 4,
        currentBeat: 0,
        isLooping: false,
        loopSectionIndex: null,
        tapTimes: [],
        sessionLogs: [],
        currentSessionId: null,
        sessionStartTime: null
      })
    }),
    {
      name: 'steelmora-rehearsal',
      partialize: (state) => ({ 
        bpm: state.bpm, 
        volume: state.volume 
      })
    }
  )
);
